import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { ModalService } from '../../../core/services/modal.service';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { NumberInputComponent } from '../../../shared/ui/forms/number-input/number-input.component';
import { SelectComponent } from '../../../shared/ui/forms/select/select.component';
import { InvoiceService } from '../../../core/services/invoice.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { Invoice } from '../../../core/models/invoice.model';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { Subscription } from 'rxjs';
import { FormFieldComponent } from '../../../shared/ui/forms/form-field/form-field.component';

@Component({
  selector: 'app-record-payment-modal',
  standalone: true,
  imports: [CommonModule, 
    ReactiveFormsModule,
    LucideIconComponent,
    ButtonComponent,
    NumberInputComponent,
    SelectComponent,
    FormFieldComponent,
    TextComponent],
  templateUrl: './record-payment-modal.component.html',
})
export class RecordPaymentModalComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private invoiceService = inject(InvoiceService);
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);

  protected readonly invoice = (this.modalService.state() as any)?.data?.invoice as Invoice;
  protected readonly onSuccess = (this.modalService.state() as any)?.data?.onSuccess;

  /** Invoice is not yet ready for payment (still draft or processing) */
  protected readonly isInvoiceNotReady = this.invoice?.status === 'draft' || this.invoice?.status === 'processing';

  form: FormGroup;
  isSaving = signal(false);
  private amountSub?: Subscription;

  constructor() {
    const defaultAmount = (this.invoice?.totalAmount || 0) - (this.invoice?.feeAmount || 0) - (this.invoice?.receivedAmount || 0);
    this.form = this.fb.group({
      amount: [defaultAmount, [Validators.required, Validators.min(0)]],
      status: ['paid', [Validators.required]]
    });
  }

  ngOnInit() {
    const defaultAmount = (this.invoice?.totalAmount || 0) - (this.invoice?.feeAmount || 0) - (this.invoice?.receivedAmount || 0);
    this.amountSub = this.form.get('amount')?.valueChanges.subscribe(val => {
      if (val !== defaultAmount) {
        // Clear status so user must explicitly choose (partially paid, overpaid, etc)
        this.form.get('status')?.setValue(null, { emitEvent: false });
      } else {
        this.form.get('status')?.setValue('paid', { emitEvent: false });
      }
    });
  }

  ngOnDestroy() {
    this.amountSub?.unsubscribe();
  }

  close() {
    this.modalService.close();
  }

  async save() {
    if (this.form.invalid || !this.invoice?.id) return;
    
    this.isSaving.set(true);
    const amount = this.form.value.amount;
    const newStatus = this.form.value.status;

    try {
      const newReceivedAmount = (this.invoice.receivedAmount || 0) + amount;
      
      const loadUpdates = (this.invoice.loadIds || []).map((id: string) => ({
        loadId: id,
        status: newStatus
      }));

      await this.invoiceService.updateInvoiceStatus(
        this.invoice.id, 
        newStatus, 
        newReceivedAmount, 
        this.invoice.feeAmount || 0,
        newStatus === 'paid' ? loadUpdates : [] // Only cascade if fully paid
      );
      this.toastService.show('success', 'Payment recorded successfully');
      this.modalService.close();
      this.onSuccess?.();
    } catch (err: any) {
      this.toastService.show('error', err.message || 'Failed to record payment');
    } finally {
      this.isSaving.set(false);
    }
  }
}
